import { useParams, Link } from "react-router-dom";
import { Check, ArrowLeft, Sparkles, Play } from "lucide-react";
import { Reveal, Eyebrow } from "../components/motion";
import { BuyBox } from "../components/BuyBox";
import { ProductCard } from "../components/ProductCard";
import { useProduct, useProducts } from "../hooks/useProducts";
import { API_URL } from "../config/site";
import { CotoneVsSeta } from "../components/CotoneVsSeta";

export default function ProductPage() {
  const { slug } = useParams();
  const { product, loading } = useProduct(slug);
  const { products } = useProducts();

  if (loading) return <main className="py-32 text-center text-plum/50">Caricamento...</main>;
  if (!product) {
    return (
      <main className="py-32 text-center">
        <p className="font-serif text-2xl text-plum">Prodotto non trovato</p>
        <Link to="/shop" className="mt-4 inline-block text-lilac-dark font-bold text-sm uppercase tracking-widest">
          Torna allo shop
        </Link>
      </main>
    );
  }

  const related = products.filter((p) => p.slug !== slug && p.category !== "kit").slice(0, 3);

  return (
    <main data-testid={`product-page-${slug}`} className="pb-20">
      <section className="bg-gradient-to-b from-lilac-soft/70 to-transparent py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-6">
          <Link to="/shop" data-testid="back-to-shop" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-plum/50 hover:text-plum transition-colors">
            <ArrowLeft className="w-4 h-4" /> Shop
          </Link>
          <div className="mt-6 grid lg:grid-cols-2 gap-12 items-center">
            <Reveal>
              <div className={`grid gap-4 ${product.image2 || product.video ? "grid-cols-2" : ""} ${product.compact_image && !product.video ? "max-w-md mx-auto w-full" : ""}`}>
                <div className="rounded-[2.5rem] overflow-hidden border-4 border-white shadow-[0_40px_80px_-30px_rgba(46,16,101,0.3)] bg-white">
                  <img src={product.image} alt={product.name} className={`w-full aspect-[4/4.2] ${product.compact_image ? "object-contain" : "object-cover"}`} data-testid="product-image" />
                </div>
                {product.image2 && (
                  <div className="rounded-[2.5rem] overflow-hidden border-4 border-white shadow-[0_40px_80px_-30px_rgba(46,16,101,0.3)] bg-white">
                    <img src={product.image2} alt={`${product.name} — texture`} className="w-full aspect-[4/4.2] object-cover" data-testid="product-image-texture" />
                  </div>
                )}
                {product.video && (
                  <div className="rounded-[2.5rem] overflow-hidden border-4 border-white shadow-[0_40px_80px_-30px_rgba(46,16,101,0.3)] bg-white">
                    <video
                      src={product.video.startsWith("/") ? `${API_URL}${product.video}` : product.video}
                      autoPlay
                      muted
                      loop
                      playsInline
                      preload="auto"
                      onCanPlay={(e) => e.target.play().catch(() => {})}
                      className={`w-full aspect-[4/4.2] ${product.video_contain ? "object-contain" : "object-cover"}`}
                      data-testid="product-video"
                    />
                  </div>
                )}
                {product.video_caption && (
                  <p
                    data-testid="video-caption"
                    className={`${product.image2 || product.video ? "col-span-2" : ""} mt-1 text-xs text-plum/60 flex items-center justify-center gap-1.5 text-center`}
                  >
                    <Play className="w-3.5 h-3.5 text-lilac-dark shrink-0" /> {product.video_caption}
                  </p>
                )}
              </div>
            </Reveal>
            <div>
              {product.scalp_type && (
                <p className="uppercase text-xs font-bold tracking-[0.22em]" style={{ color: product.accent || "#8B5CF6" }}>
                  {product.scalp_type}
                </p>
              )}
              {product.badge && !product.scalp_type && <Eyebrow>{product.badge}</Eyebrow>}
              <h1 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl text-plum tracking-tight leading-[1.12]">
                {product.name}
              </h1>
              <p className="mt-5 text-base sm:text-lg text-plum/60 leading-relaxed whitespace-pre-line">
                {product.description || product.short}
              </p>
              <div className="mt-8 bg-white rounded-3xl border border-lilac-light p-6 sm:p-8 shadow-[0_20px_60px_-30px_rgba(139,92,246,0.4)]">
                <BuyBox product={product} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 mt-14 grid md:grid-cols-2 gap-5">
        {product.includes && (
          <Reveal>
            <div className="bg-mist rounded-3xl p-8 h-full">
              <h2 className="font-serif text-xl text-plum">Cosa include il percorso</h2>
              <ul className="mt-4 space-y-2.5">
                {product.includes.map((inc) => (
                  <li key={inc} className="text-sm sm:text-base text-plum/70 flex items-center gap-2">
                    <Check className="w-4 h-4 text-lilac-dark shrink-0" /> {inc}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        )}
        {product.benefits && (
          <Reveal>
            <div className="bg-mist rounded-3xl p-8 h-full">
              <h2 className="font-serif text-xl text-plum">{product.benefits_title || "Benefici cosmetici"}</h2>
              <ul className="mt-4 space-y-2.5">
                {product.benefits.map((b) => (
                  <li key={b} className="text-sm sm:text-base text-plum/70 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-lilac-dark shrink-0" /> {b}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        )}
        {product.ingredients && (
          <Reveal delay={0.05}>
            <div className="bg-mist rounded-3xl p-8 h-full">
              <h2 className="font-serif text-xl text-plum">Ingredienti chiave</h2>
              <ul className="mt-4 space-y-2.5">
                {product.ingredients.map((ing) => (
                  <li key={ing} className="text-sm sm:text-base text-plum/70 flex items-center gap-2">
                    <Check className="w-4 h-4 text-lilac-dark shrink-0" /> {ing}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        )}
        {product.usage && (
          <Reveal delay={0.1}>
            <div className="bg-lilac-soft border border-lilac-light rounded-3xl p-8 h-full">
              <h2 className="font-serif text-xl text-plum">Modalità d'uso</h2>
              <p className="mt-4 text-sm sm:text-base text-plum/70 leading-relaxed">{product.usage}</p>
              {product.frequency && (
                <p className="mt-4 text-sm text-plum">
                  <span className="uppercase text-[10px] font-bold tracking-[0.2em] text-lilac-dark block">Frequenza consigliata</span>
                  <span className="font-serif text-lg">{product.frequency}</span>
                </p>
              )}
            </div>
          </Reveal>
        )}
      </section>

      {slug === "fibra-di-seta" && <CotoneVsSeta />}

      <section className="max-w-7xl mx-auto px-6 mt-20">
        <Reveal>
          <h2 className="font-serif text-2xl sm:text-3xl text-plum tracking-tight">Potrebbe completare la tua routine</h2>
        </Reveal>
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {related.map((p, i) => (
            <ProductCard key={p.slug} product={p} index={i} />
          ))}
        </div>
      </section>
    </main>
  );
}
